import { UserManager, WebStorageStateStore } from 'oidc-client';
import { RoleEnum } from '../../utils/api';
import {
    AuthorizationRoutes,
    ApplicationName,
    QueryParameterNames,
} from './ApiAuthorizationConstants';
import { showSessionExpiredNotification } from '../SessionExpiredNotification';

export class AuthorizeService {
    _callbacks: any[] = [];
    _nextSubscriptionId = 0;
    _user: any = null;
    _isAuthenticated = false;
    userManager: any;
    _sessionExpired = false;
    _renewPromise: Promise<any> | null = null;
    _initPromise: Promise<void> | null = null;
    _redirectingToLogin = false;

    // By default pop ups are disabled because they don't work properly on Edge.
    // If you want to enable pop up authentication simply set this flag to false.
    _popUpDisabled = true;

    constructor() {
        this.ensureUserManagerInitialized();
    }

    public renewToken() {
        // If session has expired, don't attempt to renew token
        if (this._sessionExpired) {
            return Promise.reject(new Error('Session expired. Cannot renew token.'));
        }

        return this.userManager
            .signinSilent()
            .then((u: any) => {
                this._user = u;
                this.notifySubscribers();
            })
            .catch((er: any) => {
                console.log(er);
                throw er; // Re-throw the error so callers can handle it
            });
    }

    async isAuthenticated() {
        const user = await this.getUserProfile();
        return !!user;
    }

    async getUserRole(): Promise<RoleEnum> {
        if (this._user && this._user.profile) {
            return this._user.profile.userRole as RoleEnum;
        }

        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && (user.profile.userRole as RoleEnum);
    }

    async getUserProfile() {
        if (this._user && this._user.profile) {
            return this._user.profile;
        }

        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && user.profile;
    }

    async getAccessToken() {
        // If session has expired, don't attempt to get or renew token
        if (this._sessionExpired) {
            throw new Error('Session expired. Please log in again.');
        }

        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();

        if (!user) {
            return null;
        }

        if (!user.expired) {
            return user.access_token;
        }

        // The stored token has expired (e.g. the tab was reopened after the token lifetime).
        // Try one silent renewal before giving up; concurrent callers share the same attempt.
        try {
            const renewedUser = await this.silentRenew();
            return renewedUser && renewedUser.access_token;
        } catch (error) {
            console.log('Silent token renewal failed: ', error);
            this.redirectToLogin();
            throw new Error('Authentication required. Redirecting to login.');
        }
    }

    silentRenew() {
        if (!this._renewPromise) {
            this._renewPromise = this.userManager.signinSilent().then(
                (user: any) => {
                    this._renewPromise = null;
                    this._user = user;
                    this.notifySubscribers();
                    return user;
                },
                (error: any) => {
                    this._renewPromise = null;
                    throw error;
                }
            );
        }
        return this._renewPromise;
    }

    /**
     * Sends the browser through the SPA login flow (silent sign-in first, then the IdP login
     * page) and returns to the current URL afterwards. Guarded so concurrent failed requests
     * trigger only one redirect.
     */
    redirectToLogin() {
        if (this._redirectingToLogin) {
            return;
        }
        this._redirectingToLogin = true;

        // Login.getReturnUrl requires an absolute same-origin return url.
        const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
        window.location.href = `${AuthorizationRoutes.Login}?${
            QueryParameterNames.ReturnUrl
        }=${encodeURIComponent(returnUrl)}`;
    }

    // We try to authenticate the user in three different ways:
    // 1) We try to see if we can authenticate the user silently. This happens
    //    when the user is already logged in on the IdP and is done using a hidden iframe
    //    on the client.
    // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
    //    redirect flow.
    async signIn(state: any) {
        await this.ensureUserManagerInitialized();
        try {
            const silentUser = await this.userManager.signinSilent(this.createArguments(state));
            this.updateState(silentUser);
            return this.success(state);
        } catch (silentError) {
            // User might not be authenticated, fallback to popup authentication
            console.log('Silent authentication error: ', silentError);

            try {
                if (this._popUpDisabled) {
                    throw new Error(
                        "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
                    );
                }

                const popUpUser = await this.userManager.signinPopup(this.createArguments(state));
                this.updateState(popUpUser);
                return this.success(state);
            } catch (popUpError: any) {
                if (popUpError.message === 'Popup window closed') {
                    // The user explicitly cancelled the login action by closing an opened popup.
                    return this.error('The user closed the window.');
                } else if (!this._popUpDisabled) {
                    console.log('Popup authentication error: ', popUpError);
                }

                // PopUps might be blocked by the user, fallback to redirect
                try {
                    await this.userManager.signinRedirect(this.createArguments(state));
                    return this.redirect();
                } catch (redirectError) {
                    console.log('Redirect authentication error: ', redirectError);
                    return this.error(redirectError);
                }
            }
        }
    }

    async completeSignIn(url: any) {
        try {
            await this.ensureUserManagerInitialized();
            const user = await this.userManager.signinCallback(url);
            this.updateState(user);
            return this.success(user && user.state);
        } catch (error) {
            console.log('There was an error signing in: ', error);
            return this.error('There was an error signing in.');
        }
    }

    // We try to sign out the user in two different ways:
    // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
    //    post logout redirect flow.
    async signOut(state: any) {
        await this.ensureUserManagerInitialized();

        try {
            if (this._popUpDisabled) {
                throw new Error(
                    "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
                );
            }

            await this.userManager.signoutPopup(this.createArguments(state));
            this.updateState(undefined);
            return this.success(state);
        } catch (popupSignOutError) {
            console.log('Popup signout error: ', popupSignOutError);
            try {
                await this.userManager.signoutRedirect(this.createArguments(state));
                return this.redirect();
            } catch (redirectSignOutError) {
                console.log('Redirect signout error: ', redirectSignOutError);
                return this.error(redirectSignOutError);
            }
        }
    }

    async completeSignOut(url: any) {
        await this.ensureUserManagerInitialized();
        try {
            const response = await this.userManager.signoutCallback(url);
            this.updateState(null);
            return this.success(response && response.data);
        } catch (error) {
            console.log(`There was an error trying to log out '${error}'.`);
            return this.error(error);
        }
    }

    updateState(user: any) {
        this._user = user;
        this._isAuthenticated = !!this._user;

        // Reset session expired flag when user is successfully authenticated
        if (this._user) {
            this._sessionExpired = false;
        }

        this.notifySubscribers();
    }

    /**
     * Handles session expiration by clearing user state and redirecting to login
     */
    async handleSessionExpiration() {
        try {
            // Mark session as expired to prevent further token requests
            this._sessionExpired = true;

            await this.ensureUserManagerInitialized();

            // Clear user state
            await this.userManager.removeUser();
            this.updateState(undefined);

            // Clear any cached tokens or user data
            this._user = null;
            this._isAuthenticated = false;

            // Show notification
            showSessionExpiredNotification();

            // Small delay to let user see the notification
            setTimeout(() => {
                // Redirect to login page
                const currentUrl = window.location.pathname + window.location.search;
                const loginUrl = `/Identity/Account/Login?returnUrl=${encodeURIComponent(
                    currentUrl
                )}`;
                window.location.href = loginUrl;
            }, 1000);
        } catch (error) {
            console.error('Error during session expiration handling:', error);
            // Fallback: redirect to home page
            window.location.href = '/';
        }
    }

    subscribe(callback: any) {
        this._callbacks.push({
            callback,
            subscription: this._nextSubscriptionId++,
        });
        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId: any) {
        const subscriptionIndex: any[] = this._callbacks
            .map((element, index) =>
                element.subscription === subscriptionId ? { found: true, index } : { found: false }
            )
            .filter((element) => element.found === true);
        if (subscriptionIndex.length !== 1) {
            throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
        }

        this._callbacks.splice(subscriptionIndex[0].index, 1);
    }

    notifySubscribers() {
        for (let i = 0; i < this._callbacks.length; i++) {
            const callback = this._callbacks[i].callback;
            callback();
        }
    }

    createArguments(state: any) {
        return { useReplaceToNavigate: true, data: state };
    }

    error(message: any) {
        return { status: AuthenticationResultStatus.Fail, message };
    }

    success(state: any) {
        return { status: AuthenticationResultStatus.Success, state };
    }

    redirect() {
        return { status: AuthenticationResultStatus.Redirect };
    }

    /**
     * Callers racing during start-up share one initialization; otherwise each creates its own
     * UserManager, with its own check-session iframe and silent-renew timer.
     */
    ensureUserManagerInitialized(): Promise<void> {
        if (!this._initPromise) {
            this._initPromise = this.initializeUserManager().catch((error) => {
                this._initPromise = null;
                throw error;
            });
        }
        return this._initPromise;
    }

    private async initializeUserManager() {
        let response = await fetch(AuthorizationRoutes.ApiAuthorizationClientConfigurationUrl);

        if (!response.ok) {
            throw new Error(`Could not load settings for '${ApplicationName}'`);
        }

        let settings = await response.json();
        settings.automaticSilentRenew = true;
        settings.includeIdTokenInSilentRenew = true;
        settings.silentRequestTimeout = 10000; // 10 seconds timeout
        settings.userStore = new WebStorageStateStore({
            prefix: ApplicationName,
        });

        this.userManager = new UserManager(settings);

        // Handle silent renew errors to prevent multiple iframe creation
        this.userManager.events.addSilentRenewError((error: any) => {
            console.log('Silent renew error:', error);
            // Clean up any failed silent renew attempts
        });

        this.userManager.events.addUserSignedOut(async () => {
            await this.userManager.removeUser();
            this.updateState(undefined);
        });
    }

    static get instance() {
        return authService;
    }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
    Redirect: 'redirect',
    Success: 'success',
    Fail: 'fail',
};
